import starEmpty from '@/assets/icons/star-empty.svg';
import { HSpacer, IconButton, Text, TextLink, TopAppBar, VSpacer } from '@/components/DesignSystem';
import { OrderDetailsModal } from '@/components/shared/OrderDetailsModal';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import { BookingStatusBadge } from '@/pages/CustomerDashboard/Bookings/BookingStatusBadge';
import {
  ReservedPointsBalanceCard,
} from '@/pages/CustomerDashboard/Bookings/ReservedPointsBalanceCard';
import { defaultDashboardData } from '@/pages/CustomerDashboard/CustomerDashboard';
import { useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import { DashboardApi } from '@/utilities/api/DashboardApi';
import { ApiSubwallet } from '@api/interfaces';
import { ApiOrderTransaction } from '@api/interfaces/ApiOrderTransaction';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import StarOutlined from '@mui/icons-material/StarOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, CircularProgress, Divider, Stack, useTheme } from '@mui/material';
import { OrderTransactionSubtype, TransactionType } from '@shared/enums';
import { formatMediumDate, formatNumber, getAccountName } from '@shared/utilities';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

export const BookingDetailPage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const returnToWallet = !!(location.state as { returnToWallet?: boolean } | null)?.returnToWallet;
  const bookingId = params.bookingId!;
  const [showOrders, setShowOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ApiOrderTransaction | null>(null);
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigateToPage = useDashboardNavigate();
  const { data: dashboardData } = useDashboard(accessToken);
  const { balances } = dashboardData ?? defaultDashboardData;
  const brandColor = dashboardData?.retailer.brandColor ?? theme.palette.primary.main;

  const onBack = () => {
    if (returnToWallet) {
      navigateToPage(
        Routes.CUSTOMER_DASHBOARD_WALLET,
        accessToken,
        undefined,
        { openReservedPoints: true },
      );
    } else {
      navigateToPage(Routes.CUSTOMER_DASHBOARD_BOOKINGS, accessToken);
    }
  };

  const subwallets: ApiSubwallet[] = useMemo(() => (
    [...new Map(balances.map(({ subwallet }) => [subwallet.id, subwallet])).values()]
  ), [balances]);

  const { data: booking } = useQuery(
    [QueryKeys.GET_BOOKING, accessToken, bookingId],
    () => DashboardApi.getBooking(accessToken!, bookingId),
    { enabled: !!accessToken },
  );

  const reservedPoints = booking?.pointHistories
    .filter(({ type }) => (
      type === TransactionType.BOOKING_CREDIT || type === TransactionType.BOOKING_CREDIT_CORRECTION
    ))
    .reduce((acc, { amount }) => acc + amount, 0) ?? 0;

  const uniqueOrders = Array.from(
    new Map(
      (booking?.invoicedProducts ?? [])
        .map((product) => [product.order.id, product.order]),
    ).values(),
  ).sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());

  const getRedeemedPoints = (histories: ApiPointsHistory[]) => (
    histories.filter((history) => (
      [TransactionType.BOOKING_REDEMPTION, TransactionType.BOOKING_REFUND].includes(history.type) &&
      history.orderTransactionProduct?.linkedBookingId === booking?.id
    )).reduce((acc, history) => acc + history.amount, 0) ?? 0
  );

  const totalRedeemed = Math.abs(
    getRedeemedPoints(uniqueOrders.flatMap(order => order.pointHistories ?? [])),
  );
  const remainingPoints = reservedPoints - totalRedeemed;
  const hasOrders = uniqueOrders.length > 0;
  const nonZeroBalances = booking?.subwalletBalances.filter(
    (balance) => balance.points !== 0,
  ) ?? [];

  const getOrderLabel = (order: ApiOrderTransaction) => {
    if (order.orderSubtype === OrderTransactionSubtype.SCALE_TICKET) {
      return t($ => $.orders.scaleTicket);
    } else if (order.orderSubtype === OrderTransactionSubtype.SETTLEMENT) {
      return t($ => $.common.order.settlement);
    }
    return t($ => $.common.order.order);
  };

  const OrderDetails = () => (
    <>
      <VSpacer size="4" />
      {uniqueOrders.map((order) => (
        <Stack key={order.id}>
          <Divider />
          <VSpacer size="4" />
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Text category="label-medium" whiteSpace="nowrap">
                {formatMediumDate(order.orderDate, i18n)}
              </Text>
              <VSpacer size="2" />
              <Text category="body-medium" whiteSpace="nowrap">
                {t($ => $.bookings.table.details.ptsRedeemed, {
                  points: getRedeemedPoints(order.pointHistories),
                })}
              </Text>
            </Stack>
            <HSpacer size="4" />
            <TextLink
              alignItems="flex-start"
              category="body-small"
              onClick={() => setSelectedOrder(order)}
              sx={{ color: brandColor, wordBreak: 'break-all' }}
              testID={`order-details-textlink-${order.id}`}
            >
              {getOrderLabel(order)} {order.groupingId}
            </TextLink>
          </Stack>
          <VSpacer size="4" />
        </Stack>
      ))}
    </>
  );

  const RemainingPointsSection = () => {
    if (remainingPoints > 0) {
      return (
        <>
          {!!nonZeroBalances.length && (
            <>
              <Text category="title-large">
                {t($ => $.bookings.reservedPoints.title)}
              </Text>
              <VSpacer size="4" />
              {nonZeroBalances.map((balance) => (
                <>
                  <ReservedPointsBalanceCard
                    accessToken={accessToken}
                    balance={balance}
                    brandColor={brandColor}
                    key={balance.subwalletId}
                  />
                  <VSpacer size="5" />
                </>
              ))}
            </>
          )}
        </>
      );
    }
    return (
      <Stack alignItems="center" py="16px">
        <Text category="label-large" fontSize="40px">
          🏆
        </Text>
        <VSpacer size="4" />
        <Text category="headline-large" fontWeight={500}>
          {formatNumber(totalRedeemed, i18n)}
        </Text>
        <Text category="body-medium">
          {t($ => $.bookings.reservedPoints.allRedeemed)}
        </Text>
      </Stack>
    );
  };

  const ReservedPointsSection = () => {
    if (reservedPoints > 0) {
      return <RemainingPointsSection />;
    }
    return (
      <Stack alignItems="center" py="24px">
        <img alt="star icon" src={starEmpty} />
        <VSpacer size="3" />
        <Text category="body-medium">
          {t($ => $.bookings.reservedPoints.noPointsReserved)}
        </Text>
      </Stack>
    );
  };

  return (
    <>
      <Stack
        bgcolor={theme.palette.background.default}
        flexGrow={1}
        height="auto"
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
      >
        <TopAppBar
          isStatic
          leftAccessory={(
            <IconButton
              onClick={onBack}
              sx={{ color: theme.palette.common.white }}
              testID="back-button"
            >
              <ArrowBack />
            </IconButton>
          )}
          rightAccessory={<HSpacer size="9" />}
          sx={{ bgcolor: brandColor }}
          testID="top-app-bar"
        >
          <Text category="title-large" sx={{ color: theme.palette.common.white }}>
            {booking
              ? t($ => $.orders.details.booking, { bookingId: booking.groupingId })
              : t($ => $.bookings.title)}
          </Text>
        </TopAppBar>
        {booking ? (
          <Stack p="16px">
            <Text category="overline">
              {getAccountName(booking.farmer)}
            </Text>
            <VSpacer size="1" />
            <BookingStatusBadge
              bookingData={{
                isClosed: booking.isClosed ?? false,
                expirationDate: booking.expirationDate ? new Date(booking.expirationDate) : null,
              }}
            />
            <VSpacer size="5" />
            {reservedPoints > 0 && (
              <>
                <Stack direction="row">
                  <Box
                    borderRadius="4px"
                    boxShadow="0 3px 8px 0 rgba(0, 0, 0, 0.24)"
                    flex="1"
                    p="14px 12px 16px 16px"
                  >
                    <Stack alignItems="center" direction="row" justifyContent="space-between">
                      <Text category="label-small">
                        {t($ => $.bookings.table.headers.pointsRedeemed).toUpperCase()}
                      </Text>
                      <StarOutlined
                        sx={{ color: theme.palette.rewardsYellow, height: 14, width: 14 }}
                      />
                    </Stack>
                    <VSpacer size="1" />
                    <VSpacer size="2" />
                    <Text category="headline-small" fontWeight={500}>
                      {formatNumber(totalRedeemed, i18n)}
                    </Text>
                  </Box>
                  <HSpacer size="3" />
                  <Box
                    borderRadius="4px"
                    boxShadow="0 3px 8px 0 rgba(0, 0, 0, 0.24)"
                    flex="1"
                    p="14px 12px 16px 16px"
                  >
                    <Stack alignItems="center" direction="row" justifyContent="space-between">
                      <Text category="label-small">
                        {t($ => $.bookings.table.headers.pointsRemaining).toUpperCase()}
                      </Text>
                      <StarOutlined
                        sx={{ color: theme.palette.rewardsYellow, height: 14, width: 14 }}
                      />
                    </Stack>
                    <VSpacer size="1" />
                    <VSpacer size="2" />
                    <Text category="headline-small" fontWeight={500}>
                      {formatNumber(remainingPoints, i18n)}
                    </Text>
                  </Box>
                </Stack>
                <VSpacer size="5" />
              </>
            )}
            <Box
              border="1px solid rgba(0, 0, 0, 0.12)"
              borderRadius="8px"
              p="8px 16px"
            >
              <Stack direction="row" justifyContent="space-between">
                <Text category="body-medium">
                  {t($ => $.bookings.table.headers.totalPointsReserved)}
                </Text>
                <Text category="label-large">
                  {formatNumber(reservedPoints, i18n)}
                </Text>
              </Stack>
              <VSpacer size="1" />
              <Stack direction="row" justifyContent="space-between">
                <Text category="body-medium">
                  {t($ => $.bookings.table.details.lastUpdated)}
                </Text>
                <Text category="label-large">
                  {formatMediumDate(booking.updatedAt, i18n)}
                </Text>
              </Stack>
              <VSpacer size="1" />
              <Stack direction="row" justifyContent="space-between">
                <Text category="body-medium">
                  {t($ => $.bookings.table.details.bookingCreated)}
                </Text>
                <Text category="label-large">
                  {formatMediumDate(booking.orderDate, i18n)}
                </Text>
              </Stack>
              <VSpacer size="1" />
              <Stack direction="row" justifyContent="space-between">
                <Text category="body-medium">
                  {t($ => $.bookings.table.details.bookingExpires)}
                </Text>
                <Text category="label-large">
                  {booking.expirationDate
                    ? formatMediumDate(booking.expirationDate, i18n)
                    : t($ => $.common.na)}
                </Text>
              </Stack>
              {reservedPoints > 0 && (
                <>
                  <VSpacer size="3" />
                  <Divider />
                  <VSpacer size="3" />
                  <Stack
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    onClick={() => hasOrders && setShowOrders(!showOrders)}
                    sx={{ cursor: hasOrders ? 'pointer' : 'default' }}
                  >
                    <Stack alignItems="center" direction="row">
                      {hasOrders ? (
                        <>
                          <ReceiptLongOutlined
                            sx={{ color: brandColor, height: '16px', width: '16px' }}
                          />
                          <HSpacer size="3" />
                          <Text category="label-large" sx={{ color: brandColor }}>
                            {t($ => $.common.order.ordersCount, { count: uniqueOrders.length })}
                          </Text>
                        </>
                      ) : (
                        <>
                          <ReceiptLongOutlined sx={{ height: '16px', width: '16px' }} />
                          <HSpacer size="3" />
                          <Text category="body-medium">
                            {t($ => $.bookings.table.details.noOrders)}
                          </Text>
                        </>
                      )}
                    </Stack>
                    {hasOrders && (
                      <>
                        {showOrders
                          ? <KeyboardArrowUp sx={{ color: brandColor }} />
                          : <KeyboardArrowDown sx={{ color: brandColor }} />
                        }
                      </>
                    )}
                  </Stack>
                  {showOrders && <OrderDetails />}
                </>
              )}
            </Box>
            <VSpacer size="7" />
            <VSpacer size="3" />
            <ReservedPointsSection />
          </Stack>
        ) : (
          <Stack alignItems="center" justifyContent="center" minHeight="200px">
            <CircularProgress />
          </Stack>
        )}
      </Stack>
      {!!selectedOrder && (
        <OrderDetailsModal
          accessToken={accessToken}
          brandColor={brandColor}
          forceMobileView
          onClose={() => setSelectedOrder(null)}
          orderId={selectedOrder.id}
          subwallets={subwallets}
        />
      )}
    </>
  );
};
